import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { CategorieDossier } from '@repere/core';
import { Alerte, Bouton, ChampJours, Picto } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { dateComplete, jourEtMois } from './dates';
import { periodesDemandees } from './periodes';
import {
  LIBELLE_STATUT,
  LIBELLE_TYPE,
  type Proposition,
  type StatutProposition,
} from './types';

/**
 * Demandes d'accord du dossier.
 *
 * Trois règles de formulation, toutes issues des principes anti-conflit :
 *
 * - un statut décrit un fait, jamais une personne : « Déclinée », pas « Refusée par X » ;
 * - une demande en attente n'est jamais présentée comme un retard de l'autre parent —
 *   il n'y a ni compteur de jours, ni bouton de relance ;
 * - le motif de refus reste facultatif, comme côté serveur : l'exiger ferait de chaque
 *   « non » une pièce à charge.
 */
/**
 * La catégorie du dossier que chaque type de demande touche.
 *
 * Elle sert à choisir le pictogramme : un échange de jours et une dérogation concernent le
 * calendrier, une clé de répartition et une contestation concernent les dépenses. Le
 * dessin situe, le titre précise.
 */
const CATEGORIE_DU_TYPE: Record<Proposition['type'], CategorieDossier> = {
  schedule_change: 'calendrier',
  day_swap: 'calendrier',
  exception: 'calendrier',
  split_rule_change: 'depenses',
  expense_dispute: 'depenses',
};

/**
 * L'état d'une demande, en couleur.
 *
 * La liste était en gris uniforme : trois demandes empilées se distinguaient seulement en
 * lisant leur ligne d'état. Le vert et le rouge donnent la réponse avant la lecture.
 *
 * **Le rouge est employé ici comme signal**, ce que la charte lui refusait jusqu'à présent
 * (docs/01 §3 : « le rouge est décoratif, jamais sémantique »). C'est un choix assumé sur
 * cet écran-là, et il est tenu au plus discret : une pastille teintée, pas une carte
 * inondée, et un libellé qui reste « Déclinée » — jamais « Refusée par Dominique ».
 */
const CLASSE_STATUT: Record<StatutProposition, string> = {
  pending: 'attente',
  accepted: 'acceptee',
  declined: 'declinee',
  countered: 'contree',
  expired: 'echue',
};

/**
 * Les demandes du dossier.
 *
 * Exportée pour que le volet d'« Aujourd'hui » sache s'il a quelque chose à montrer avant
 * d'écrire son intitulé (`VoletDemandes`, dans `routes/Dossier.tsx`) : une liste vide n'y
 * paraît pas du tout. La clé est celle de la liste elle-même — le volet et la liste
 * partagent donc le même cache, et React Query ne charge qu'une fois.
 */
export function usePropositions(caseFileId: string) {
  const { client } = useSession();
  return useQuery({
    queryKey: ['proposals', caseFileId],
    queryFn: () => client.request<Proposition[]>(`/case-files/${caseFileId}/proposals`),
  });
}

export function Propositions({
  caseFileId,
  monUserId,
}: {
  caseFileId: string;
  monUserId: string | null;
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [contreProposition, setContreProposition] = useState<string | null>(null);
  const [nouveauxJours, setNouveauxJours] = useState<string[]>([]);

  const propositions = usePropositions(caseFileId);

  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['proposals', caseFileId] });
    void queryClient.invalidateQueries({ queryKey: ['occurrences', caseFileId] });
    void queryClient.invalidateQueries({ queryKey: ['qui', caseFileId] });
  };

  const repondre = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'decline' }) =>
      action === 'accept'
        ? client.request(`/case-files/${caseFileId}/custody/proposals/${id}/accept`, {
            method: 'POST',
          })
        : client.request(`/proposals/${id}/decline`, { method: 'POST', body: {} }),
    onSuccess: rafraichir,
  });

  const contrer = useMutation({
    mutationFn: (id: string) =>
      client.request(`/proposals/${id}/counter`, {
        method: 'POST',
        body: { payload: { periods: periodesDemandees(nouveauxJours, 2) } },
      }),
    onSuccess: () => {
      setContreProposition(null);
      setNouveauxJours([]);
      rafraichir();
    },
  });

  if (propositions.isPending) return <p className="discret">Chargement des demandes…</p>;

  if (propositions.isError) {
    return (
      <Alerte titre="Les demandes n’ont pas pu être chargées">
        {messageDErreur(propositions.error)}
      </Alerte>
    );
  }

  // Rien, et pas un bloc vide. Ce qu'il faut dire quand il n'y a aucune demande se décide
  // chez celui qui écrit l'intitulé — `VoletDemandes` ne se rend pas du tout — et une
  // seconde formulation ici finirait par le contredire. La garde reste pour qui monterait
  // cette liste seule, hors du volet : mieux vaut ne rien peindre qu'un `<ul>` sans ligne.
  if (propositions.data.length === 0) return null;

  return (
    <>
      {repondre.isError ? (
        <Alerte titre="La réponse n’a pas pu être enregistrée">{messageDErreur(repondre.error)}</Alerte>
      ) : null}

      <ul className="accords">
        {propositions.data.map((proposition) => {
          const jeSuisLAuteur = monUserId !== null && proposition.authorId === monUserId;
          const enAttente = proposition.status === 'pending';
          const periodes = (proposition.payload.periods ?? []) as {
            from?: string;
            to?: string;
          }[];

          return (
            <li key={proposition.id} className="accord">
              {/* Le pictogramme dit **quelle partie du dossier** la demande touche — le
                  calendrier, les dépenses — et non le type exact, que le titre nomme
                  déjà. Inventer cinq dessins pour cinq nuances d'un même sujet aurait
                  fabriqué cinq symboles à apprendre pour rien. */}
              <div className="accord-entete">
                <Picto categorie={CATEGORIE_DU_TYPE[proposition.type]} taille={20} />
                <h3>{LIBELLE_TYPE[proposition.type]}</h3>
                <span className={`statut-accord ${CLASSE_STATUT[proposition.status]}`}>
                  {LIBELLE_STATUT[proposition.status]}
                </span>
              </div>

              {/* La période et la date de la demande tiennent sur une seule ligne. Elles
                  occupaient deux paragraphes pleine largeur chacun, pour une dizaine de
                  mots — et trois demandes empilées faisaient défiler tout l'écran. */}
              <p className="accord-resume discret">
                {periodes.length > 0
                  ? `${periodes
                      .map((p) =>
                        p.from && p.to
                          ? `du ${jourEtMois(new Date(p.from))} au ${jourEtMois(new Date(p.to))}`
                          : '—',
                      )
                      .join(', ')} · `
                  : ''}
                demandée le {dateComplete(new Date(proposition.createdAt))}
              </p>

              {proposition.reason ? (
                <p className="accord-motif">Motif indiqué : {proposition.reason}</p>
              ) : null}

                {enAttente && jeSuisLAuteur ? (
                  <p className="discret" style={{ marginBottom: 0 }}>
                    Votre demande attend une réponse. Vous serez informé dès qu'elle en
                    recevra une.
                  </p>
                ) : null}

                {enAttente && !jeSuisLAuteur ? (
                  <>
                    <Bouton
                      onClick={() => repondre.mutate({ id: proposition.id, action: 'accept' })}
                      disabled={repondre.isPending}
                    >
                      Accepter
                    </Bouton>{' '}
                    <Bouton
                      variante="secondaire"
                      onClick={() => repondre.mutate({ id: proposition.id, action: 'decline' })}
                      disabled={repondre.isPending}
                    >
                      Décliner
                    </Bouton>{' '}
                    <Bouton
                      variante="secondaire"
                      onClick={() =>
                        setContreProposition(
                          contreProposition === proposition.id ? null : proposition.id,
                        )
                      }
                    >
                      Proposer autre chose
                    </Bouton>

                    {contreProposition === proposition.id ? (
                      <div style={{ marginTop: 'var(--espace-md)' }}>
                        <ChampJours
                          label="Nouveaux jours proposés"
                          valeur={nouveauxJours}
                          onChange={setNouveauxJours}
                          fenetre={{ avant: 1, apres: 1 }}
                          aide="Touchez les jours voulus. Ils n'ont pas à se suivre."
                        />
                        <Bouton
                          onClick={() => contrer.mutate(proposition.id)}
                          disabled={nouveauxJours.length === 0 || contrer.isPending}
                        >
                          Envoyer la contre-proposition
                        </Bouton>
                      </div>
                    ) : null}
                  </>
                ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}
